import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Coins, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { rewards, type CoinsResponse, type CoinTransaction } from '../api/client';
import { useInfiniteList, rowCursor } from '../hooks/useInfiniteList';
import SEO from '../components/SEO';
import Breadcrumb from '../components/Breadcrumb';
import CoinStatement from '../components/CoinStatement';
import CoinExpiryStatement from '../components/CoinExpiryStatement';
import { expiryChipLabel } from '../components/CoinSummaryCard';

type Tab = 'statement' | 'expiring';

export default function MyCoinsPage() {
  const { user, isLoading } = useAuth();
  const { isDark } = useTheme();
  const [tab, setTab] = useState<Tab>('statement');
  const [coins, setCoins] = useState<CoinsResponse | null>(null);

  // Statement list drives the summary too — the first page carries balance +
  // expiring_this_month (same pattern as the Rewards page).
  const coinActivity = useInfiniteList<CoinTransaction>(
    async (cursor) => {
      const r = await rewards.coins(cursor);
      if (!cursor) setCoins(r);
      return { items: r.history, hasMore: r.has_more };
    },
    (t) => rowCursor(t.created_at, t.id),
    user ? user.id : null,
    !!user,
  );

  if (!isLoading && !user) return <Navigate to="/login" replace />;

  const card = isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200';
  const sub = isDark ? 'text-gray-400' : 'text-gray-500';
  const available = coins?.balance ?? 0;
  const expiring = coins?.expiring_this_month?.amount ?? 0;
  const expOn = coins?.expiring_this_month?.on;

  return (
    <div className={`min-h-screen ${isDark ? 'bg-gray-950 text-gray-100' : 'bg-gray-50 text-gray-900'}`}>
      <SEO title="My ZS Coins — ZeroSmoke" description="Your ZS Coins balance, upcoming expiry and full activity statement." />
      <Breadcrumb items={[
        { name: 'Home', href: '/' },
        { name: 'Rewards', href: '/rewards' },
        { name: 'My ZS Coins' },
      ]} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Link to="/rewards" className={`inline-flex items-center gap-2 mb-6 text-sm ${isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-500 hover:text-gray-700'}`}>
          <ArrowLeft size={16} /> Back
        </Link>
        <h1 className="text-2xl sm:text-3xl font-extrabold mb-6">My ZS Coins</h1>

        {/* stat cards */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className={`rounded-2xl border p-5 ${card}`}>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center mb-3 shadow-sm">
              <Coins size={18} className="text-white" />
            </div>
            <p className={`text-xs ${sub}`}>Available Coins</p>
            <p className="text-2xl font-extrabold mt-0.5">{available.toLocaleString('en-IN')}</p>
          </div>
          <div className={`relative rounded-2xl border p-5 ${card}`}>
            <span className="absolute top-3 right-3 whitespace-nowrap text-xs font-bold px-3 py-1 rounded-xl bg-blue-600 text-white shadow-md">
              Exp in {expiryChipLabel(expOn)}
            </span>
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-rose-400 to-rose-600 flex items-center justify-center mb-3 shadow-sm">
              <Clock size={18} className="text-white" />
            </div>
            <p className={`text-xs ${sub}`}>Expiring Coins</p>
            <p className="text-2xl font-extrabold mt-0.5">{expiring.toLocaleString('en-IN')}</p>
          </div>
        </div>

        {/* tabs */}
        <div className={`flex gap-1 mb-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-200'}`}>
          {([['statement', 'Statement'], ['expiring', 'Expiring']] as const).map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              className={`px-5 py-2.5 text-sm font-semibold -mb-px border-b-2 transition-colors ${tab === k ? 'border-emerald-500 text-emerald-500' : `border-transparent ${sub}`}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'statement' ? (
          <CoinStatement items={coinActivity.items} loading={coinActivity.loading} onScroll={coinActivity.onScroll} maxHeight="max-h-[60vh]" />
        ) : (
          <CoinExpiryStatement resetKey={user?.id} maxHeight="max-h-[60vh]" />
        )}
      </div>
    </div>
  );
}
